import { useCallback, startTransition } from 'react'
import { useNavigate } from 'react-router-dom'
import type { OpenTab } from '@/components/features/document-tabs'
import type { ViewMode } from '@/components/layout/document-viewer.types'
import { markTabClickNavigation } from '@/hooks/use-url-sync'
import {
  ensureHomeTabInOpenTabs,
  isSoleHomeTab,
  isTabVisibleForActiveWorkspaces,
} from '@/components/layout/resizable-layout.handlers/shared'
import { useWorkspace } from '@/providers/workspace-provider'

/**
 * Return type for use-tab-management hook
 */
export interface UseTabManagementReturn {
  handleTabClick: (tabId: string) => void
  handleTabClose: (tabId: string) => void
  handleTabReorder: (reorderedTabs: OpenTab[]) => void
  handleTabPinToggle: (tabId: string) => void
  handleTabDragToChat: (tabId: string, title: string, type: OpenTab["type"]) => void
}

/**
 * Hook for managing tab operations (click, close, reorder, pin, drag)
 */
export function useTabManagement({
  openTabs,
  setOpenTabs,
  activeTabId,
  setActiveTabId,
  setTabBarActiveId,
  setViewMode,
  navigation,
  onDocumentDragToChat,
}: {
  openTabs: OpenTab[]
  setOpenTabs: React.Dispatch<React.SetStateAction<OpenTab[]>>
  activeTabId: string
  setActiveTabId: (id: string) => void
  setTabBarActiveId?: (id: string | null) => void
  setViewMode: (mode: ViewMode) => void
  navigation: ReturnType<typeof import('@/hooks/use-navigation-history').usePerTabNavigationHistory>
  onDocumentDragToChat?: (tabId: string, title: string, type: OpenTab["type"]) => void
}): UseTabManagementReturn {
  const navigate = useNavigate()
  const { workspaces, activeWorkspaceIds } = useWorkspace()

  /**
   * Handle tab click - switch to the clicked tab
   * Uses startTransition so tab bar can paint before heavy content re-renders.
   * Defers navigation.navigate to avoid synchronous history state write in click handler.
   *
   * Bug 1 fix (2026-05-08): previously called
   *   navigation.navigate(tabId, ...)
   * which used the document id as the history slot key, while back/forward
   * looks up history by tabSlotId. The mismatch left every tab-click history
   * entry orphaned and invisible to back/forward. Now we resolve the target
   * tab's tabSlotId and skip the write entirely if the slot already points at
   * this document (avoids duplicating the current entry on every tab switch).
   */
  const handleTabClick = useCallback((tabId: string) => {
    // Mark as tab click navigation so URL updates after document loads
    markTabClickNavigation()

    // Sync update for instant tab bar feedback (paints in same frame)
    setTabBarActiveId?.(tabId)

    const targetTab = openTabs.find((t) => t.id === tabId)
    const slotId = targetTab?.tabSlotId ?? "tab-main"
    const explorerFolder =
      targetTab?.type === "explorer" ? (targetTab.explorerData?.folderPath ?? null) : undefined

    // Leave /edit-history immediately when switching away so URL sync cannot yank back.
    if (
      (activeTabId === 'edit-history' || activeTabId === 'view:edit-history') &&
      tabId !== 'edit-history' &&
      tabId !== 'view:edit-history'
    ) {
      if (targetTab?.type === 'search-view') {
        navigate('/search', { replace: true })
      } else if (targetTab?.type === 'home') {
        navigate('/', { replace: true })
      } else if (targetTab?.type === 'person-view' && targetTab.personUserId) {
        navigate(`/people/${encodeURIComponent(targetTab.personUserId)}`, { replace: true })
      } else if (targetTab?.type === 'person-view') {
        navigate('/people', { replace: true })
      }
    }

    // Defer navigation to next tick - avoids blocking click handler with history state write
    queueMicrotask(() => {
      const slotState = navigation.tabNavigationState[slotId]
      const currentEntry = slotState?.history[slotState.currentIndex]
      // Tab switches that land on a slot whose current entry already matches
      // should NOT push a duplicate. Without this guard, switching back and
      // forth between two tabs would inflate each slot's history on every
      // click, eventually breaking back/forward semantics.
      if (explorerFolder !== undefined) {
        const alreadyAtFolder =
          currentEntry?.viewMode === "list" &&
          (currentEntry?.folderPath ?? null) === explorerFolder
        if (!alreadyAtFolder) {
          navigation.navigate(
            slotId,
            { documentId: null, folderPath: explorerFolder, viewMode: "list" },
            "tab-click-explorer",
          )
        }
        return
      }
      const alreadyAtTarget =
        currentEntry?.documentId === tabId && currentEntry?.viewMode === "single"
      if (!alreadyAtTarget) {
        navigation.navigate(slotId, { documentId: tabId, viewMode: "single" }, "tab-click")
      }
    })

    // Low-priority update: content loads in background
    startTransition(() => {
      setActiveTabId(tabId)
      setViewMode(explorerFolder !== undefined ? "list" : "single")
    })
  }, [openTabs, activeTabId, setActiveTabId, setTabBarActiveId, setViewMode, navigation, navigate])

  /**
   * Handle tab close - close tab and switch to another if needed
   */
  const handleTabClose = useCallback((tabId: string) => {
    if (isSoleHomeTab(openTabs, tabId)) return

    // Allow closing Explorer tabs - they can be reopened from sidebar
    // Mark this as a tab click navigation to prevent URL sync from re-opening the tab
    markTabClickNavigation()

    // Notify ChatSurfaceRegistry so it clears the binding for this thread.
    if (tabId.startsWith('chat-conversation-')) {
      window.dispatchEvent(new CustomEvent('chat-conversation-tab-user-closed', { detail: { tabId } }))
    }

    const newOpenTabs = openTabs.filter(tab => tab.id !== tabId)
    setOpenTabs(newOpenTabs)

    // If closing active tab, switch to another in-scope tab or home.
    // Never promote a parked (out-of-scope) tab - that deletes the stack one-by-one.
    if (tabId === activeTabId) {
      const inScope = newOpenTabs.filter((tab) =>
        isTabVisibleForActiveWorkspaces(tab, workspaces, activeWorkspaceIds),
      )
      if (inScope.length > 0) {
        const newTab = inScope[inScope.length - 1]
        const newActiveId = newTab.id
        setActiveTabId(newActiveId)
        if (newTab.type === 'explorer') {
          navigation.navigate(newTab.tabSlotId || 'tab-main', { documentId: null, folderPath: newTab.explorerData?.folderPath || null, viewMode: 'list' }, "tab-close-fallback-explorer")
        } else if (newTab.type === 'home') {
          navigation.navigate(newTab.tabSlotId || 'tab-main', { documentId: null, viewMode: 'home' }, "tab-close-fallback-home")
          navigate('/', { replace: true })
        } else if (newTab.type === 'search-view') {
          navigation.navigate(newTab.tabSlotId || `tab-${newActiveId}`, { documentId: newActiveId, viewMode: "single" }, "tab-close-fallback-search")
          navigate('/search', { replace: true })
        } else if (newTab.type === 'person-view' && newTab.personUserId) {
          navigation.navigate(newTab.tabSlotId || `tab-${newActiveId}`, { documentId: newActiveId, viewMode: "single" }, "tab-close-fallback-person")
          navigate(`/people/${encodeURIComponent(newTab.personUserId)}`, { replace: true })
        } else if (newTab.type === 'person-view') {
          navigation.navigate(newTab.tabSlotId || `tab-${newActiveId}`, { documentId: newActiveId, viewMode: "single" }, "tab-close-fallback-people")
          navigate('/people', { replace: true })
        } else if (newTab.type === 'edit-history' || newActiveId === 'edit-history') {
          navigation.navigate(newTab.tabSlotId || `tab-${newActiveId}`, { documentId: newActiveId, viewMode: "single" }, "tab-close-fallback-activity")
          navigate('/edit-history', { replace: true })
        } else {
          navigation.navigate(newTab.tabSlotId || `tab-${newActiveId}`, { documentId: newActiveId, viewMode: "single" }, "tab-close-fallback-doc")
        }
      } else {
        // No in-scope tabs left - switch to home (keep parked tabs in openTabs)
        setOpenTabs(ensureHomeTabInOpenTabs(newOpenTabs))
        setActiveTabId('home')
        navigation.navigate('tab-main', { documentId: null, viewMode: 'home' }, "tab-close-no-tabs")
        navigate('/', { replace: true })
      }
    }
  }, [openTabs, setOpenTabs, activeTabId, setActiveTabId, navigation, navigate, workspaces, activeWorkspaceIds])

  /**
   * Handle tab reorder - update tabs after drag and drop
   */
  const handleTabReorder = useCallback((reorderedTabs: OpenTab[]) => {
    setOpenTabs(reorderedTabs)
  }, [setOpenTabs])

  /**
   * Handle tab pin toggle - pin/unpin a tab
   */
  const handleTabPinToggle = useCallback((tabId: string) => {
    // Allow pinning all tabs including Explorer
    const updatedTabs = openTabs.map(tab =>
      tab.id === tabId
        ? { ...tab, isPinned: !tab.isPinned }
        : tab
    )

    setOpenTabs(updatedTabs)
  }, [openTabs, setOpenTabs])

  /**
   * Handle tab drag to chat - add document to chat references
   */
  const handleTabDragToChat = useCallback((tabId: string, title: string, type: OpenTab["type"]) => {
    onDocumentDragToChat?.(tabId, title, type)
  }, [onDocumentDragToChat])

  return {
    handleTabClick,
    handleTabClose,
    handleTabReorder,
    handleTabPinToggle,
    handleTabDragToChat,
  }
}
